import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Users, Tag, Settings, LayoutDashboard, LogOut,
  Search, Trash2, CheckCircle2, XCircle, Plus, Loader2,
  ChevronLeft, ChevronRight, RefreshCw, TrendingUp,
  CreditCard, Utensils, UserCheck, Shield, AlertTriangle,
  X, Check, Mail, ChevronDown, Eye, ToggleLeft, ToggleRight,
  ArrowLeft, Calendar, Send, Menu, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_cf802d02-7a21-497a-89b7-2bcd31caeb90/artifacts/1chviqu2_Screenshot%202026-06-21%20at%2019.30.33-Photoroom.png';

const fmt = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

const fmtFull = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleString('bg-BG') : '—';

// ── Shared Pagination ──────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onPage }) => (
  <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-slate-700">
    <p className="text-xs text-gray-400">Страница {page} от {totalPages}</p>
    <div className="flex gap-2">
      <button disabled={page === 1} onClick={() => onPage(page - 1)}
        className="w-8 h-8 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 dark:bg-slate-900">
        <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>
      <button disabled={page === totalPages} onClick={() => onPage(page + 1)}
        className="w-8 h-8 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 dark:bg-slate-900">
        <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>
    </div>
  </div>
);

// ── Confirm Modal ──────────────────────────────────────────────────────────────
const ConfirmModal = ({ title, message, confirmLabel = 'Изтрий', onConfirm, onCancel, loading, danger = true }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-2xl p-6 w-full max-w-sm"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${danger ? 'bg-red-50' : 'bg-amber-50'}`}>
          <AlertTriangle className={`w-4.5 h-4.5 ${danger ? 'text-red-500' : 'text-amber-500'}`} style={{ width: 18, height: 18 }} />
        </div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">{message}</p>
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-slate-900 transition-colors">
          Отказ
        </button>
        <button onClick={onConfirm} disabled={loading}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
        </button>
      </div>
    </motion.div>
  </div>
);

// ── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color }) => {
  const c = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-600' },
  }[color] || { bg: 'bg-gray-50 dark:bg-slate-900', text: 'text-gray-600 dark:text-gray-300' };
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
      <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center mb-4`}>
        <Icon className={`${c.text}`} style={{ width: 18, height: 18 }} />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums leading-none">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
};

// ── User Detail Drawer ─────────────────────────────────────────────────────────
const UserDetailDrawer = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/users/${userId}/detail`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Грешка при зареждане'))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="relative bg-white dark:bg-slate-800 w-full max-w-lg shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Детайли за потребителя</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 flex items-center justify-center">
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : data ? (
            <>
              {/* User Info */}
              <div className="bg-gray-50 dark:bg-slate-900 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">{data.user.full_name?.[0] || data.user.email[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{data.user.full_name || '—'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{data.user.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-2.5">
                    <p className="text-gray-400 mb-0.5">Регистрация</p>
                    <p className="font-medium text-gray-700 dark:text-gray-200">{fmt(data.user.created_at)}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl p-2.5">
                    <p className="text-gray-400 mb-0.5">Абонамент</p>
                    <p className={`font-semibold ${data.user.subscription_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {data.user.subscription_active ? 'Активен' : 'Неактивен'}
                    </p>
                  </div>
                  {data.user.subscription_active && data.user.subscription_expires_at && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-2.5 col-span-2">
                      <p className="text-gray-400 mb-0.5">Изтича</p>
                      <p className="font-medium text-gray-700 dark:text-gray-200">{fmtFull(data.user.subscription_expires_at)}</p>
                    </div>
                  )}
                  {data.user.is_admin && (
                    <div className="bg-emerald-50 rounded-xl p-2.5 col-span-2 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-emerald-600" />
                      <p className="text-xs font-semibold text-emerald-700">Администратор</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Plans */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Планове ({data.plans.length})
                </p>
                {data.plans.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">Няма генерирани планове</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.plans.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-slate-900 rounded-xl px-3.5 py-2.5">
                        <div>
                          <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{p.goal} — {p.diet}</p>
                          <p className="text-[10px] text-gray-400">{fmt(p.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{p.target_calories} ккал</p>
                          {p.is_preview && <span className="text-[9px] text-amber-500 font-medium">Превю</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payments */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Плащания ({data.payments.length})
                </p>
                {data.payments.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">Няма плащания</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-slate-900 rounded-xl px-3.5 py-2.5">
                        <div>
                          <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                            {p.payment_status === 'paid' ? 'Платено' : 'Изчакване'}
                          </p>
                          <p className="text-[10px] text-gray-400">{fmtFull(p.created_at)}</p>
                        </div>
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-200 tabular-nums">{Number(p.amount || 0).toFixed(2)} €</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
};

// ── Dashboard Tab ──────────────────────────────────────────────────────────────
const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/analytics?days=30`),
    ]).then(([sRes, aRes]) => {
      setStats(sRes.data);
      setAnalytics(aRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const shortDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  const chartData = analytics.map(d => ({
    ...d,
    label: shortDate(d.date),
  }));

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-emerald-500 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Общо потребители" value={stats?.total_users ?? 0}
          sub={`+${stats?.new_users_7d ?? 0} тази седмица`} color="blue" />
        <StatCard icon={UserCheck} label="Активни абонати" value={stats?.active_subscriptions ?? 0} color="emerald" />
        <StatCard icon={TrendingUp} label="Приходи (€)" value={`${(stats?.total_revenue ?? 0).toFixed(2)}`} color="amber" />
        <StatCard icon={Utensils} label="Генерирани планове" value={stats?.total_plans ?? 0} color="violet" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Нови потребители (30 дни)</p>
          <p className="text-xs text-gray-400 mb-5">Дневни регистрации</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false}
                interval={Math.floor(chartData.length / 6)} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 12 }}
                formatter={(v) => [v, 'Потребители']} labelFormatter={(l) => l} />
              <Area type="monotone" dataKey="users" stroke="#059669" strokeWidth={2}
                fill="url(#usersGrad)" dot={false} activeDot={{ r: 4, fill: '#059669' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Приходи (30 дни)</p>
          <p className="text-xs text-gray-400 mb-5">Дневни приходи в €</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false}
                interval={Math.floor(chartData.length / 6)} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 12 }}
                formatter={(v) => [`${v.toFixed(2)} €`, 'Приходи']} />
              <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ── Users Tab ──────────────────────────────────────────────────────────────────
const UsersTab = ({ currentAdminId }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [subModal, setSubModal] = useState(null); // { user, action: 'activate'|'deactivate' }
  const [subDays, setSubDays] = useState(30);
  const [genModal, setGenModal] = useState(null); // { user }
  const [genLoading, setGenLoading] = useState(false);
  const limit = 15;

  const load = useCallback(async (p = 1, s = '') => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`, { params: { page: p, limit, search: s } });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch { toast.error('Грешка при зареждане'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line
  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line

  const toggleSub = async () => {
    if (!subModal) return;
    const { user, action } = subModal;
    setActionLoading(p => ({ ...p, [user.id]: true }));
    try {
      await axios.patch(`${API}/admin/users/${user.id}/subscription`, {
        status: action === 'activate' ? 'active' : 'inactive',
        days: subDays,
      });
      toast.success(action === 'activate' ? `Абонаментът е активиран (${subDays} дни)` : 'Абонаментът е деактивиран');
      setSubModal(null);
      load(page, search);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setActionLoading(p => ({ ...p, [user.id]: false })); }
  };

  const toggleAdmin = async (user) => {
    setActionLoading(p => ({ ...p, [`admin_${user.id}`]: true }));
    try {
      const r = await axios.patch(`${API}/admin/users/${user.id}/admin`);
      toast.success(r.data.is_admin ? 'Правата на администратор са дадени' : 'Правата са отнети');
      load(page, search);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setActionLoading(p => ({ ...p, [`admin_${user.id}`]: false })); }
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${confirmDelete.id}`);
      toast.success('Потребителят е изтрит');
      setConfirmDelete(null);
      load(page, search);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setDeleteLoading(false); }
  };

  const handleGenerations = async (action, amount = 1) => {
    if (!genModal) return;
    setGenLoading(true);
    try {
      const r = await axios.patch(`${API}/admin/users/${genModal.user.id}/generations`, { action, amount });
      toast.success(r.data.message);
      setGenModal(null);
      load(page, search);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setGenLoading(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Потребители</h2>
          <p className="text-xs text-gray-400">{total} общо</p>
        </div>
        <button onClick={() => load(page, search)}
          className="w-8 h-8 border border-gray-200 dark:border-slate-600 rounded-lg flex items-center justify-center hover:bg-gray-50 dark:bg-slate-900">
          <RefreshCw className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Търси по имейл или ime..."
          data-testid="admin-user-search"
          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-4 py-2.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
          {['Потребител', 'Планове', 'Регистрация', 'Абонамент', 'Действия'].map(h => (
            <p key={h} className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{h}</p>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">Няма намерени потребители</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {users.map(u => (
              <div key={u.id} data-testid="admin-user-row"
                className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center px-4 py-3 hover:bg-gray-50 dark:bg-slate-900/60 transition-colors">
                <div className="min-w-0 cursor-pointer" onClick={() => setDetailUserId(u.id)}>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{u.full_name || '—'}</p>
                    {u.is_admin && <Shield className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center w-12">{u.plans_count ?? 0}</p>
                <p className="text-xs text-gray-400 w-18 text-center">{fmt(u.created_at)}</p>
                <div className="w-22 flex justify-center">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    u.subscription_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {u.subscription_active ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
                <div className="flex items-center gap-1 justify-end">
                  {/* View detail */}
                  <button onClick={() => setDetailUserId(u.id)} title="Детайли"
                    className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-blue-50 hover:border-blue-200 transition-all">
                    <Eye className="w-3.5 h-3.5 text-blue-500" />
                  </button>
                  {/* Toggle subscription */}
                  <button
                    onClick={() => { setSubDays(30); setSubModal({ user: u, action: u.subscription_active ? 'deactivate' : 'activate' }); }}
                    disabled={!!actionLoading[u.id] || u.is_admin}
                    title={u.subscription_active ? 'Деактивирай' : 'Активирай абонамент'}
                    data-testid={`admin-toggle-sub-${u.id}`}
                    className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-emerald-50 hover:border-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    {actionLoading[u.id]
                      ? <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                      : u.subscription_active
                        ? <XCircle className="w-3.5 h-3.5 text-red-400" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    }
                  </button>
                  {/* Generation reset */}
                  <button
                    onClick={() => setGenModal({ user: u })}
                    disabled={u.is_admin}
                    title="Управление на генерации"
                    data-testid={`admin-gen-${u.id}`}
                    className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-amber-50 hover:border-amber-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                  </button>
                  {/* Toggle admin */}
                  <button
                    onClick={() => toggleAdmin(u)}
                    disabled={!!actionLoading[`admin_${u.id}`] || u.id === currentAdminId}
                    title={u.is_admin ? 'Отнеми админ права' : 'Дай админ права'}
                    data-testid={`admin-toggle-admin-${u.id}`}
                    className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-violet-50 hover:border-violet-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    {actionLoading[`admin_${u.id}`]
                      ? <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                      : <Shield className={`w-3.5 h-3.5 ${u.is_admin ? 'text-violet-500' : 'text-gray-400'}`} />
                    }
                  </button>
                  {/* Delete */}
                  <button onClick={() => setConfirmDelete(u)} disabled={u.is_admin}
                    data-testid={`admin-delete-user-${u.id}`}
                    title="Изтрий" className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-red-50 hover:border-red-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPage={p => { setPage(p); }} />}

      {/* Subscription Modal */}
      {subModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {subModal.action === 'activate' ? 'Активирай абонамент' : 'Деактивирай абонамент'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{subModal.user.email}</p>
            {subModal.action === 'activate' && (
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5 block">Брой дни</label>
                <input type="number" min="1" max="3650" value={subDays}
                  onChange={e => setSubDays(Number(e.target.value))}
                  className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setSubModal(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-slate-900">
                Отказ
              </button>
              <button onClick={toggleSub}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-sm font-semibold text-white flex items-center justify-center gap-2">
                {actionLoading[subModal.user.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Потвърди'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal title="Изтрий потребителя"
          message={`Сигурни ли сте? Всички данни на "${confirmDelete.email}" ще бъдат изтрити завинаги.`}
          loading={deleteLoading} onConfirm={deleteUser} onCancel={() => setConfirmDelete(null)} />
      )}

      {/* Generation Modal */}
      {genModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-2xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Генерации</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500">{genModal.user.email}</p>
              </div>
            </div>
            <div className="space-y-2 mb-5">
              <button
                onClick={() => handleGenerations('reset')}
                disabled={genLoading}
                data-testid={`admin-gen-reset-${genModal.user.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 hover:border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all text-left group"
              >
                <RefreshCw className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Нулирай лимита</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Връща броя на генерациите на 0 за текущия период</p>
                </div>
                {genLoading && <Loader2 className="w-3.5 h-3.5 animate-spin ml-auto text-gray-400" />}
              </button>
              <button
                onClick={() => handleGenerations('add', 1)}
                disabled={genLoading}
                data-testid={`admin-gen-add-${genModal.user.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 hover:border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all text-left"
              >
                <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">+1 бонус генерация</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Добавя постоянна бонус генерация към месечния лимит</p>
                </div>
              </button>
            </div>
            <button onClick={() => setGenModal(null)}
              className="w-full py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
              Затвори
            </button>
          </motion.div>
        </div>
      )}

      <AnimatePresence>
        {detailUserId && <UserDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} />}
      </AnimatePresence>
    </div>
  );
};

// ── Plans Tab ──────────────────────────────────────────────────────────────────
const PlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async (p = 1, s = '') => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/plans`, { params: { page: p, limit, search: s } });
      setPlans(r.data.plans);
      setTotal(r.data.total);
    } catch { toast.error('Грешка'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line
  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Хранителни планове</h2>
          <p className="text-xs text-gray-400">{total} общо</p>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Търси по имейл на потребителя..."
          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
      </div>
      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-4 py-2.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
          {['Потребител', 'Калории', 'Цел', 'Диета', 'Дата'].map(h => (
            <p key={h} className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{h}</p>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-400">Няма планове</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {plans.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center px-4 py-3 hover:bg-gray-50 dark:bg-slate-900/60">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{p.user_name || '—'}</p>
                  <p className="text-[10px] text-gray-400 truncate">{p.user_email}</p>
                </div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 text-right w-20">{p.target_calories} ккал</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 w-24 truncate">{p.goal}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 w-20">{p.diet}</p>
                <div className="flex items-center gap-1.5 justify-end">
                  <p className="text-xs text-gray-400">{fmt(p.created_at)}</p>
                  {p.is_preview && <span className="text-[9px] bg-amber-50 text-amber-600 font-semibold px-1.5 py-0.5 rounded-full">Превю</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {Math.ceil(total / limit) > 1 && <Pagination page={page} totalPages={Math.ceil(total / limit)} onPage={setPage} />}
    </div>
  );
};

// ── Payments Tab ───────────────────────────────────────────────────────────────
const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/payments`, { params: { page: p, limit } });
      setPayments(r.data.payments);
      setTotal(r.data.total);
    } catch { toast.error('Грешка'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  const totalRevenue = payments.filter(p => p.payment_status === 'paid').reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Плащания</h2>
          <p className="text-xs text-gray-400">{total} общо</p>
        </div>
        {payments.length > 0 && (
          <div className="text-right">
            <p className="text-xs text-gray-400">Показани</p>
            <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{totalRevenue.toFixed(2)} €</p>
          </div>
        )}
      </div>
      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-4 py-2.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
          {['Потребител', 'Сума', 'Статус', 'Дата'].map(h => (
            <p key={h} className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{h}</p>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>
        ) : payments.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-400">Няма плащания</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {payments.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-4 py-3 hover:bg-gray-50 dark:bg-slate-900/60">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{p.user_name || '—'}</p>
                  <p className="text-[10px] text-gray-400 truncate">{p.user_email}</p>
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums text-right">{Number(p.amount || 0).toFixed(2)} €</p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-center ${
                  p.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  {p.payment_status === 'paid' ? 'Платено' : 'Изчакване'}
                </span>
                <p className="text-xs text-gray-400 text-right">{fmt(p.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {Math.ceil(total / limit) > 1 && <Pagination page={page} totalPages={Math.ceil(total / limit)} onPage={setPage} />}
    </div>
  );
};

// ── Promo Tab ──────────────────────────────────────────────────────────────────
const PromoTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [toggling, setToggling] = useState({});
  const [form, setForm] = useState({ code: '', discount_percent: 10, label: '', new_price: '' });

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/promo`); setPromos(r.data); }
    catch { toast.error('Грешка'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.label || !form.new_price) { toast.error('Попълни всички полета'); return; }
    setCreateLoading(true);
    try {
      await axios.post(`${API}/admin/promo`, { code: form.code, discount_percent: Number(form.discount_percent), label: form.label, new_price: Number(form.new_price) });
      toast.success('Промо кодът е създаден');
      setForm({ code: '', discount_percent: 10, label: '', new_price: '' });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setCreateLoading(false); }
  };

  const togglePromo = async (code) => {
    setToggling(p => ({ ...p, [code]: true }));
    try {
      const r = await axios.patch(`${API}/admin/promo/${code}/toggle`);
      toast.success(r.data.active ? 'Кодът е активиран' : 'Кодът е деактивиран');
      load();
    } catch { toast.error('Грешка'); }
    finally { setToggling(p => ({ ...p, [code]: false })); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/promo/${deleteTarget}`);
      toast.success('Промо кодът е изтрит');
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка'); }
    finally { setDeleteLoading(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Промо кодове</h2>
          <p className="text-xs text-gray-400">{promos.length} кода</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} data-testid="admin-add-promo-btn"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-sm font-semibold text-white transition-colors">
          <Plus className="w-4 h-4" /> Нов код
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <form onSubmit={handleCreate} className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 mb-5">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Нов промо код</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Код *</label>
                  <input type="text" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="SUMMER25" data-testid="admin-promo-code-input"
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm font-mono tracking-widest focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Отстъпка %</label>
                  <input type="number" min="1" max="99" value={form.discount_percent}
                    onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))}
                    data-testid="admin-promo-discount-input"
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Описание *</label>
                  <input type="text" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="25% отстъпка лято" data-testid="admin-promo-label-input"
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Нова цена (€) *</label>
                  <input type="number" step="0.01" min="0.50" value={form.new_price}
                    onChange={e => setForm(f => ({ ...f, new_price: e.target.value }))}
                    placeholder="5.99" data-testid="admin-promo-price-input"
                    className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-white dark:bg-slate-800" />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-slate-900">Отказ</button>
                <button type="submit" disabled={createLoading} data-testid="admin-promo-submit-btn"
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-sm font-semibold text-white flex items-center justify-center gap-2">
                  {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Създай'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-2 px-5 py-2.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
            {['Код', 'Описание', 'Отстъпка', 'Нова цена', 'Статус', ''].map(h => (
              <p key={h} className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{h}</p>
            ))}
          </div>
          {promos.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">Няма промо кодове</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {promos.map(p => (
                <div key={p.code} data-testid={`admin-promo-row-${p.code}`}
                  className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-2 items-center px-5 py-3.5 hover:bg-gray-50 dark:bg-slate-900/60">
                  <span className="font-mono text-sm font-bold text-emerald-700 tracking-widest">{p.code}</span>
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{p.label}</p>
                  <span className="bg-amber-50 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">-{p.discount_percent}%</span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 tabular-nums w-14 text-right">{Number(p.new_price).toFixed(2)} €</span>
                  <button onClick={() => togglePromo(p.code)} disabled={!!toggling[p.code]}
                    data-testid={`admin-promo-toggle-${p.code}`}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                      p.active ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200'
                    }`}>
                    {toggling[p.code] ? <Loader2 className="w-3 h-3 animate-spin" /> : p.active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                    {p.active ? 'Активен' : 'Неактивен'}
                  </button>
                  <button onClick={() => setDeleteTarget(p.code)} data-testid={`admin-promo-delete-${p.code}`}
                    className="w-7 h-7 rounded-lg border border-gray-200 dark:border-slate-600 flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-all">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="Изтрий промо код"
          message={`Сигурни ли сте, че искате да изтриете кода "${deleteTarget}"?`}
          loading={deleteLoading} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
};

// ── Email Tab ──────────────────────────────────────────────────────────────────
const EmailTab = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('all');
  const [customEmail, setCustomEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Попълни темата и съдържанието'); return; }
    const t = target === 'custom' ? customEmail.trim() : target;
    if (target === 'custom' && !customEmail.trim()) { toast.error('Въведи конкретен имейл'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/email`, { subject, html: body, target: t });
      toast.success(r.data.message);
      setSubject(''); setBody(''); setCustomEmail('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Грешка при изпращане'); }
    finally { setSending(false); }
  };

  const TARGETS = [
    { value: 'all', label: 'Всички потребители' },
    { value: 'active', label: 'Само активни абонати' },
    { value: 'inactive', label: 'Само неактивни' },
    { value: 'custom', label: 'Конкретен имейл' },
  ];

  return (
    <div className="max-w-2xl">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Изпрати имейл</h2>
        <p className="text-xs text-gray-400 mt-0.5">Масов имейл до потребители чрез Resend</p>
      </div>

      <div className="space-y-4">
        {/* Target */}
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm">
          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase tracking-wider">До кого</label>
          <div className="grid grid-cols-2 gap-2">
            {TARGETS.map(t => (
              <button key={t.value} onClick={() => setTarget(t.value)}
                data-testid={`admin-email-target-${t.value}`}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-all text-left ${
                  target === t.value
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-slate-900'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          {target === 'custom' && (
            <input type="email" value={customEmail} onChange={e => setCustomEmail(e.target.value)}
              placeholder="email@example.com"
              className="mt-3 w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
          )}
        </div>

        {/* Subject */}
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm">
          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase tracking-wider">Тема</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="напр. Специална оферта само за тебe"
            data-testid="admin-email-subject"
            className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
        </div>

        {/* Body */}
        <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Съдържание (HTML)</label>
            <button onClick={() => setPreview(s => !s)}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> {preview ? 'Скрий' : 'Предварителен преглед'}
            </button>
          </div>
          <textarea rows={10} value={body} onChange={e => setBody(e.target.value)}
            placeholder="<p>Здравей,</p><p>Специална оферта само за теб...</p>"
            data-testid="admin-email-body"
            className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-emerald-400 resize-none" />
          {preview && body && (
            <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 text-sm"
              dangerouslySetInnerHTML={{ __html: body }} />
          )}
        </div>

        <button onClick={send} disabled={sending} data-testid="admin-email-send-btn"
          className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-colors">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Изпрати имейла
        </button>
      </div>
    </div>
  );
};

// ── Settings Tab ───────────────────────────────────────────────────────────────
const SettingsTab = () => {
  const [settings, setSettings] = useState({ subscription_price_eur: 7.99, subscription_duration_days: 30, site_notice: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`).then(r => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, settings);
      toast.success('Настройките са запазени');
    } catch { toast.error('Грешка'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>;

  return (
    <div className="max-w-xl space-y-5">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Настройки</h2>

      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Абонамент</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Цена (€/месец)</label>
            <input type="number" step="0.01" min="0.99" value={settings.subscription_price_eur}
              onChange={e => setSettings(s => ({ ...s, subscription_price_eur: Number(e.target.value) }))}
              data-testid="admin-settings-price"
              className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
            <p className="text-[10px] text-gray-400 mt-1">Показва се в UI — Stripe цената е отделна</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">Продължителност (дни)</label>
            <input type="number" min="1" value={settings.subscription_duration_days}
              onChange={e => setSettings(s => ({ ...s, subscription_duration_days: Number(e.target.value) }))}
              data-testid="admin-settings-duration"
              className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Банер известие</p>
        <textarea rows={3} value={settings.site_notice}
          onChange={e => setSettings(s => ({ ...s, site_notice: e.target.value }))}
          placeholder="Добави съобщение за потребителите (оставете празно за да скриете)..."
          data-testid="admin-settings-notice"
          className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 resize-none" />
        <p className="text-[10px] text-gray-400 mt-1">Показва се като банер на сайта</p>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Достъп на администратор</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          Управлявайте администраторите от таблото с потребители — използвайте иконата <Shield className="w-3.5 h-3.5 inline text-violet-500" /> за дадете или отнемете права на всеки потребител.
        </p>
      </div>

      <button onClick={save} disabled={saving} data-testid="admin-settings-save-btn"
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Запази настройките
      </button>
    </div>
  );
};

// ── Main Admin Page ────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Общ преглед', icon: LayoutDashboard },
  { id: 'users',     label: 'Потребители',  icon: Users },
  { id: 'plans',     label: 'Планове',      icon: Utensils },
  { id: 'payments',  label: 'Плащания',     icon: CreditCard },
  { id: 'promo',     label: 'Промо кодове', icon: Tag },
  { id: 'email',     label: 'Имейл',        icon: Mail },
  { id: 'settings',  label: 'Настройки',    icon: Settings },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex">
      {/* ── Sidebar ── */}
      <>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-56 bg-white dark:bg-slate-800 border-r border-gray-100 dark:border-slate-700 flex flex-col
          transition-transform duration-200 lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Logo */}
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
            <div>
              <button onClick={() => navigate('/')} className="block focus:outline-none" data-testid="admin-logo-home-btn">
                <img src={LOGO_URL} alt="portion.bg" className="h-7 dark:brightness-0 dark:invert" />
              </button>
              <p className="text-[9px] text-gray-400 mt-1 font-semibold uppercase tracking-widest">Администрация</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden w-7 h-7 rounded-lg hover:bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => { setTab(t.id); setSidebarOpen(false); }}
                  data-testid={`admin-nav-${t.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 ${
                    active
                      ? 'bg-emerald-50 text-emerald-700 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-slate-900 hover:text-gray-900 dark:text-gray-100'
                  }`}>
                  <Icon style={{ width: 15, height: 15 }} className="flex-shrink-0" />
                  {t.label}
                </button>
              );
            })}
          </nav>

          {/* User */}
          <div className="px-3 py-4 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-2.5 mb-2 px-1">
              <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <span className="text-[11px] font-bold text-white">{user?.email?.[0]?.toUpperCase()}</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{user?.full_name || 'Admin'}</p>
                <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button onClick={() => navigate('/')}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:bg-slate-900 transition-all">
              <ArrowLeft className="w-3.5 h-3.5" /> Към сайта
            </button>
            <button onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-red-500 hover:text-red-600 hover:bg-red-50 transition-all">
              <LogOut className="w-3.5 h-3.5" /> Изход
            </button>
          </div>
        </aside>
      </>

      {/* ── Main ── */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-8 h-8 border border-gray-200 dark:border-slate-600 rounded-lg flex items-center justify-center hover:bg-gray-50 dark:bg-slate-900">
              <Menu className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{TABS.find(t => t.id === tab)?.label}</h1>
              <p className="text-xs text-gray-400 hidden sm:block">portion.bg Admin Panel</p>
            </div>
          </div>
          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-200 transition-colors border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:bg-slate-900">
            <ArrowLeft className="w-3.5 h-3.5" /> Към сайта
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5 sm:p-6">
          <AnimatePresence mode="wait">
            <motion.div key={tab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}>
              {tab === 'dashboard' && <DashboardTab />}
              {tab === 'users'     && <UsersTab currentAdminId={user?.id} />}
              {tab === 'plans'     && <PlansTab />}
              {tab === 'payments'  && <PaymentsTab />}
              {tab === 'promo'     && <PromoTab />}
              {tab === 'email'     && <EmailTab />}
              {tab === 'settings'  && <SettingsTab />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
